// "Traer de Mercado Libre": listar las publicaciones del vendedor e importarlas como
// productos. El listado y el import los hace atlas-backend con el token de la conexión;
// acá sólo se pide y se sigue el job (ADR-2026-09-28-mercadolibre-importar-por-el-atributo-de-la-foto).

import { atlasBackendFetch, atlasBackendGet, pollJobUntilSettled } from "./atlasBackend";
import { codigoDeError, ErrorDeMercadoLibre } from "./mercadoLibreConexionLogica";
import type { ListadoDeMl, ResultadoDelImport } from "./mercadoLibreCatalogoLogica";

async function leerJson(r: Response): Promise<unknown> {
  try {
    return await r.json();
  } catch {
    return null;
  }
}

/** Publicaciones activas (o pausadas) del vendedor, ya agrupadas. Tarda unos segundos. */
export async function listarPublicacionesDeMl(
  brandId: string | null,
  estado: "active" | "paused" = "active",
  /** false = una entrada por publicación (para subir fotos: en el modelo nuevo son por combinación). */
  agrupar = true,
): Promise<ListadoDeMl> {
  const q = new URLSearchParams({ estado, agrupar: agrupar ? "si" : "no" });
  if (brandId) q.set("brand_id", brandId);
  let r: Response;
  try {
    r = await atlasBackendGet(`/v1/mercadolibre/publicaciones?${q.toString()}`);
  } catch {
    throw new ErrorDeMercadoLibre("red");
  }
  const cuerpo = await leerJson(r);
  if (!r.ok) throw new ErrorDeMercadoLibre(codigoDeError(r.status, cuerpo));
  const d = (cuerpo ?? {}) as Partial<ListadoDeMl>;
  return {
    publicaciones: Array.isArray(d.publicaciones) ? d.publicaciones : [],
    total: typeof d.total === "number" ? d.total : 0,
    truncado: d.truncado === true,
  };
}

/** Encola el import. Devuelve el id de la ejecución para seguirla. */
export async function importarDeMl(
  brandId: string | null,
  elegidas: Array<{ item_ids: string[]; producto_id?: string | null }>,
): Promise<string> {
  let r: Response;
  try {
    r = await atlasBackendFetch("/v1/mercadolibre/importar", { brand_id: brandId, publicaciones: elegidas });
  } catch {
    throw new ErrorDeMercadoLibre("red");
  }
  const cuerpo = await leerJson(r);
  if (!r.ok) throw new ErrorDeMercadoLibre(codigoDeError(r.status, cuerpo));
  const id = (cuerpo as { execution_id?: unknown } | null)?.execution_id;
  if (typeof id !== "string") throw new ErrorDeMercadoLibre("error");
  return id;
}

/**
 * Espera el import. null = se agotó la espera (sigue corriendo en el servidor: los productos
 * aparecen solos). Si falló, tira con el mensaje del backend.
 */
export async function esperarImportDeMl(executionId: string, signal?: AbortSignal): Promise<ResultadoDelImport | null> {
  const r = await pollJobUntilSettled(executionId, { timeoutMs: 5 * 60_000, intervalMs: 2500, signal });
  if (!r) return null;
  if (r.status === "failed") {
    const e = new ErrorDeMercadoLibre(r.errorType === "auth" ? "reconectar" : "error");
    if (r.userMessage) e.message = r.userMessage;
    throw e;
  }
  return (r.output ?? null) as ResultadoDelImport | null;
}
